/**
 * Client-side platform registry.
 *
 * Adding a new platform (e.g. TikTok) means adding one entry here
 * and implementing the backend provider. Icon components and CSS
 * classes live in the frontend, so this registry cannot be served
 * from the API.
 */

import { IconProps } from "@/components/icons/types";
import { FacebookIcon, InstagramIcon, TikTokIcon } from "@/components/icons";

export interface PlatformMetricLabels {
  likesLabel: string;
  sharesLabel: string;
}

export interface PlatformConfig {
  key: string;
  label: string;
  color: string;
  iconBgClass: string;
  iconTextClass: string;
  iconComponent: React.ComponentType<IconProps>;
  publishKey: string;
  metrics: PlatformMetricLabels;
  /** Engagement metric keys used by the backend for this platform's posts */
  engagementKeys: {
    likes: string;
    shares: string;
  };
  /** Key for the follower count in platform report metrics */
  followersKey: string;
  /** Whether this platform is selected by default when publishing */
  defaultEnabled: boolean;
  /** Whether publishing is available for this platform */
  canPublish: boolean;
  /** Reason publishing is unavailable (shown in UI when canPublish is false) */
  publishDisabledReason?: string;
  /** Whether publishing sends to drafts instead of posting directly */
  publishAsDraft?: boolean;
}

export const PLATFORM_REGISTRY: Record<string, PlatformConfig> = {
  facebook: {
    key: "facebook",
    label: "Facebook",
    color: "#1877F2",
    iconBgClass: "bg-blue-100",
    iconTextClass: "text-blue-600",
    iconComponent: FacebookIcon,
    publishKey: "facebook-page",
    metrics: {
      likesLabel: "Reactions",
      sharesLabel: "Shares",
    },
    engagementKeys: {
      likes: "reactions",
      shares: "shares",
    },
    followersKey: "totalFans",
    defaultEnabled: true,
    canPublish: true,
  },
  instagram: {
    key: "instagram",
    label: "Instagram",
    color: "#E1306C",
    iconBgClass: "bg-linear-to-br from-purple-500 to-pink-500",
    iconTextClass: "text-white",
    iconComponent: InstagramIcon,
    publishKey: "instagram-feed",
    metrics: {
      likesLabel: "Likes",
      sharesLabel: "Saved",
    },
    engagementKeys: {
      likes: "likes",
      shares: "saved",
    },
    followersKey: "totalFollowers",
    defaultEnabled: true,
    canPublish: true,
  },
  tiktok: {
    key: "tiktok",
    label: "TikTok",
    color: "#000000",
    iconBgClass: "bg-gray-900",
    iconTextClass: "text-white",
    iconComponent: TikTokIcon,
    publishKey: "tiktok-video",
    metrics: {
      likesLabel: "Likes",
      sharesLabel: "Shares",
    },
    engagementKeys: {
      likes: "likes",
      shares: "shares",
    },
    followersKey: "totalFollowers",
    defaultEnabled: false,
    canPublish: true,
    publishAsDraft: true,
  },
};

/**
 * Get platform config by key. Returns undefined for unknown platforms.
 */
export function getPlatformConfig(
  platform: string
): PlatformConfig | undefined {
  return PLATFORM_REGISTRY[platform];
}

/**
 * Get all registered platform keys.
 */
export function getPlatformKeys(): string[] {
  return Object.keys(PLATFORM_REGISTRY);
}

/**
 * Get platform keys where defaultEnabled is true.
 * Used for initial checkbox state in publish/schedule UIs.
 */
export function getDefaultPlatformKeys(): string[] {
  return Object.entries(PLATFORM_REGISTRY)
    .filter(([, config]) => config.defaultEnabled)
    .map(([key]) => key);
}
